// FILE: src/Components/Onboarding.js (FULLY UPDATED)
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Spin, message, Progress } from 'antd';
import Organization from './Organization';
import AssetsData from './AssetsData';
import { getProfileDetails, checkOnboardingStatus } from '../services/group';
import { handleOnboarding } from '../services/user.profile';
import {
  LoadingOutlined,
  ExclamationCircleOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setAns1, setAns2, setAns3 } from '../store/Slices/OnboardingSlice';
import { fetchGroups } from '../store/Slices/GroupSlice';

const OptionCard = ({ label, isSelected, onClick }) => (
  <button
    onClick={onClick}
    className={`relative group flex items-center justify-center p-4 h-16 w-full text-sm font-semibold rounded-xl border transition-all duration-300 ease-out ${
      isSelected
        ? 'border-brand-primary bg-purple-50 text-brand-primary shadow-md ring-1 ring-purple-200'
        : 'border-gray-200 bg-white text-gray-600 hover:border-purple-300 hover:shadow-sm hover:-translate-y-0.5'
    }`}
  >
    {label}
    {isSelected && (
      <div className="absolute top-1/2 right-3 transform -translate-y-1/2 text-purple-600">
        <CheckCircleFilled />
      </div>
    )}
  </button>
);

export const AiProcessingScreen = ({
  statusText,
  progress = 0,
  estimatedSeconds = 52,
}) => {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6">
      <div className="w-full max-w-xl text-center">
        <div className="relative w-24 h-24 mx-auto mb-8">
          <div className="absolute inset-0 bg-purple-100 rounded-full animate-ping opacity-75"></div>
          <div className="relative bg-white p-6 rounded-full shadow-xl border border-purple-50 flex items-center justify-center h-full w-full">
            <LoadingOutlined style={{ fontSize: 40, color: '#7C3AED' }} spin />
          </div>
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight">
          Analyzing your brand…
        </h2>

        <p className="text-sm font-medium text-purple-600 mb-6 bg-purple-50 inline-block px-4 py-2 rounded-full">
          {statusText}
        </p>

        <div className="mb-3">
          <Progress
            percent={Math.round(progress)}
            strokeColor="#7C3AED"
            trailColor="#E9D5FF"
            showInfo={false}
          />
        </div>

        <div className="flex justify-between text-xs text-gray-500 mb-6">
          <span>{Math.round(progress)}%</span>
          <span>Usually takes about {estimatedSeconds} seconds</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left max-w-lg mx-auto mb-6">
          {[
            'Scanning website structure',
            'Extracting logos and assets',
            'Identifying brand colors',
            'Generating business summary',
          ].map((item, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2"
            >
              <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse"></span>
              {item}
            </div>
          ))}
        </div>

        <p className="text-xs text-gray-400">
          Please keep this page open while we process your brand.
        </p>
      </div>
    </div>
  );
};

export default function Onboarding() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const assetsRef = useRef();

  const { groups } = useSelector((state) => state.groups);

  const [answers, setAnswers] = useState({ role: '', industry: '', goal: '' });
  const [finalOrgData, setFinalOrgData] = useState({ Brandname: '', Brandwebsite: '' });
  const [currentStep, setCurrentStep] = useState(1);
  const [processingMessage, setProcessingMessage] = useState('Initializing AI Agents...');
  const [isSaving, setIsSaving] = useState(false);
  const [showManualInputBanner, setShowManualInputBanner] = useState(false);

  const [progress, setProgress] = useState(0);
  const [isBackendDone, setIsBackendDone] = useState(false);

  const roles = ['Content Creator', 'Marketing Agency', 'Small Business', 'Social Media Mgr.'];
  const industries = [
    'Health & Fitness',
    'Technology',
    'Beauty & Skin',
    'Finance',
    'Education',
    'Food & Beverage',
    'Retail / E-comm',
    'Real Estate',
  ];
  const goals = ['Brand Awareness', 'Lead Generation', 'Grow Followers', 'Sales / Revenue'];

  const mapDbToUiStructure = (dbData) => {
    if (!dbData) return {};
    return {
      ...dbData,
      Brandname: dbData.brand_name || '',
      Brandwebsite: dbData.website || '',
      Brandaddress: dbData.brand_address || '',
      Brandsize: dbData.brand_size || '',
      Brandcountry: dbData.country || '',
      Brandtimezone: dbData.time_zone || '',
      BusinessOverview: dbData.describes || '',
      mobile: dbData.brand_contact || '',
      groupId: dbData.groupId,
      palette: dbData.palette || '',
      website_image_url: dbData.website_image_url || [],
    };
  };

  const handleGenerateDNA = () => {
    if (!answers.role || !answers.industry || !answers.goal) {
      message.warning('Please answer all 3 questions to proceed.');
      return;
    }
    dispatch(setAns1(answers.role));
    dispatch(setAns2(answers.industry));
    dispatch(setAns3(answers.goal));
    setCurrentStep(2);
  };

  const handleOrgDataChange = (newDataFunc) => {
    setFinalOrgData((prev) => {
      const newData = typeof newDataFunc === 'function' ? newDataFunc(prev) : newDataFunc;
      return { ...prev, ...newData };
    });
  };

  const handleFinalSave = async () => {
    setIsSaving(true);
    try {
      const assetsData = assetsRef.current?.getFinalAssetsData() || {};
      const payload = {
        ...finalOrgData,
        ...assetsData,
        brand_name: finalOrgData.Brandname,
        website: finalOrgData.Brandwebsite,
        describes: finalOrgData.BusinessOverview,
        groupId: finalOrgData.groupId,
        role: answers.role,
        industry: answers.industry,
        top_goal: answers.goal,
        country: finalOrgData.Brandcountry,
        time_zone: finalOrgData.Brandtimezone,
      };
      await handleOnboarding(payload);
      message.success('Setup Complete! Welcome to Oolook.');
      navigate('/select-plan');
    } catch (error) {
      message.error('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const groupId = useMemo(() => groups?.[0]?._id, [groups]);

  useEffect(() => {
    let progressInterval;

    if (currentStep === 2) {
      setProgress(0);
      setIsBackendDone(false);

      progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (isBackendDone) {
            return prev >= 100 ? 100 : Math.min(prev + 8, 100);
          }

          if (prev < 15) return prev + 3;
          if (prev < 40) return prev + 2;
          if (prev < 65) return prev + 1.4;
          if (prev < 85) return prev + 0.9;
          if (prev < 92) return prev + 0.35;
          return prev;
        });
      }, 1000);
    }

    return () => clearInterval(progressInterval);
  }, [currentStep, isBackendDone]);

  useEffect(() => {
    if (currentStep !== 2 || !groupId) return;

    let pollingInterval;
    let messageInterval;
    let isMounted = true;

    const handleJobCompletion = async (status) => {
      if (!isMounted) return;

      setIsBackendDone(true);
      setProgress((prev) => (prev < 95 ? 95 : prev));

      setTimeout(async () => {
        if (!isMounted) return;
        setProgress(100);
        message.loading({
          content: 'Analysis complete! Loading your brand kit...',
          duration: 1.5,
        });

        await dispatch(fetchGroups());
        const profileDetails = await getProfileDetails(groupId);
        setFinalOrgData(mapDbToUiStructure(profileDetails.OnBoarding?.[0] || {}));

        if (status === 'failed') {
          message.warning(
            'AI analysis encountered an issue. Please complete your brand kit manually.',
            6
          );
          setShowManualInputBanner(true);
        }

        setTimeout(() => {
          if (isMounted) setCurrentStep(3);
        }, 800);
      }, 1200);
    };

    const pollStatus = async () => {
      try {
        const result = await checkOnboardingStatus(groupId);

        if (!isMounted) {
          clearInterval(pollingInterval);
          return;
        }

        if (result.status === 'completed' || result.status === 'failed') {
          clearInterval(pollingInterval);
          clearInterval(messageInterval);
          handleJobCompletion(result.status);
        }
      } catch (error) {
        console.error('Polling error:', error);
        if (isMounted) {
          message.error('Could not check brand status. Polling stopped.');
        }
        clearInterval(pollingInterval);
        clearInterval(messageInterval);
      }
    };

    const messages = [
      'Scanning website structure...',
      'Extracting visual assets...',
      'Analyzing brand colors...',
      'Generating mission statement...',
      'Finalizing business DNA...',
    ];

    let msgIndex = 0;
    messageInterval = setInterval(() => {
      if (isMounted) {
        setProcessingMessage(messages[msgIndex % messages.length]);
        msgIndex++;
      }
    }, 4000);

    pollStatus();
    pollingInterval = setInterval(pollStatus, 5000);

    return () => {
      isMounted = false;
      clearInterval(pollingInterval);
      clearInterval(messageInterval);
    };
  }, [currentStep, groupId, dispatch]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-sans text-gray-800">
      {currentStep === 1 && (
        <div className="flex flex-col items-center justify-center min-h-screen p-6">
          <div className="w-full max-w-4xl">
            <div className="text-center mb-10">
              <h2 className="text-4xl font-extrabold text-gray-900 mb-3 tracking-tight">
                Let&apos;s Get Started
              </h2>
              <p className="text-lg text-gray-500">
                Help us tailor the AI to your specific needs.
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-12 space-y-10">
              <div className="space-y-4">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest pl-1">
                  1. Which describes you best?
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {roles.map((r) => (
                    <OptionCard
                      key={r}
                      label={r}
                      isSelected={answers.role === r}
                      onClick={() => setAnswers((p) => ({ ...p, role: r }))}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest pl-1">
                  2. What is your industry?
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {industries.map((i) => (
                    <OptionCard
                      key={i}
                      label={i}
                      isSelected={answers.industry === i}
                      onClick={() => setAnswers((p) => ({ ...p, industry: i }))}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest pl-1">
                  3. What is your primary goal?
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {goals.map((g) => (
                    <OptionCard
                      key={g}
                      label={g}
                      isSelected={answers.goal === g}
                      onClick={() => setAnswers((p) => ({ ...p, goal: g }))}
                    />
                  ))}
                </div>
              </div>

              <div className="pt-6 flex justify-center">
                <button
                  onClick={handleGenerateDNA}
                  disabled={!answers.role || !answers.industry || !answers.goal}
                  className="bg-brand-primary text-white text-lg font-bold py-4 px-16 rounded-full shadow-lg shadow-purple-200 hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50 disabled:hover:scale-100 disabled:shadow-none flex items-center gap-3"
                >
                  Generate Business DNA <i className="fa-solid fa-wand-magic-sparkles"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <AiProcessingScreen
          statusText={processingMessage}
          progress={progress}
          estimatedSeconds={52}
        />
      )}

      {currentStep === 3 && (
        <div className="max-w-[1600px] mx-auto pb-12 pt-6 px-6">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8 bg-white p-4 rounded-xl shadow-sm border border-gray-200 sticky top-4 z-50">
            <div>
              <h1 className="text-2xl font-extrabold text-gray-900">Your Business DNA</h1>
              <p className="text-sm text-gray-500">Review and refine your brand identity.</p>
            </div>
            <button
              onClick={handleFinalSave}
              className="mt-4 md:mt-0 bg-green-600 text-white font-bold py-2.5 px-8 rounded-lg shadow-md hover:bg-green-700 hover:shadow-lg transition flex items-center gap-2"
            >
              {isSaving ? (
                <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: '#fff' }} spin />} />
              ) : (
                <>
                  Save & Finish <i className="fa-solid fa-check"></i>
                </>
              )}
            </button>
          </div>

          {showManualInputBanner && (
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-8 rounded-r-md flex items-start animate-fade-in-up">
              <ExclamationCircleOutlined className="text-amber-500 text-xl mt-0.5 mr-3" />
              <div>
                <p className="text-sm text-amber-800 font-bold">Manual Input Required</p>
                <p className="text-sm text-amber-700 mt-1">
                  We couldn&apos;t automatically scrape all details. Please review and fill in
                  missing info.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div className="w-full">
              <Organization
                dataOrganization={finalOrgData}
                onDataChange={handleOrgDataChange}
              />
            </div>

            <AssetsData
              ref={assetsRef}
              dataOrganization={finalOrgData}
              onDataChange={handleOrgDataChange}
              groupId={finalOrgData.groupId}
            />
          </div>
        </div>
      )}
    </div>
  );
}